import { ReactElement, Fragment } from 'react';
import { AddOutlined } from '@mui/icons-material';
import { Grid, Stack, Divider } from '@mui/material';
import { LabelledText } from 'components/LabelledText/LabelledText';
import { Panel } from 'components/Panel/Panel';
import { ShrinkToIconButton } from 'components/ShrinkToIconButton/ShrinkToIconButton';
import { useTranslation } from 'react-i18next';
import { Zone, ZoneDetails } from 'types/domainTypes';
import { DropdownOption, MoreDropdown } from 'components/Menu/MoreDropdown';

interface ZonesViewProps {
  zoneDetails: ZoneDetails;
  onClickAdd: () => void;
  onClickEdit: (zone: Zone) => void;
  onClickRemove: (zoneId: string) => void;
}

export function ZonesView({ zoneDetails: zones, onClickAdd, onClickEdit, onClickRemove }: ZonesViewProps): ReactElement | null {
  const { t } = useTranslation();

  return (
    <Panel
      data-testid="zones-panel"
      panelTitle={t('calculation-page.zone-view.title')}
      panelSummary={t('common.summary-format', zones)}
      panelButton={
        <ShrinkToIconButton buttonText={t('calculation-page.zone-view.add-another')} icon={<AddOutlined />} onClick={onClickAdd} />
      }
      disableSpacing
    >
      {zones.zones.map((zone, index, arr) => (
        <Fragment key={index}>
          <Grid item xs={11}>
            <Stack spacing={2} divider={<Divider />}>
              <Stack spacing={2}>
                <LabelledText label="calculation-page.zone-view.label-name" value={zone.name} />
                <Stack direction="row" justifyContent="space-between">
                  <LabelledText label="calculation-page.zone-view.label-number-of-spans" value={zone.spans.toString()} />
                  <LabelledText label="calculation-page.zone-view.label-span-lengths" value={zone.spanLength.toString()} />
                  <LabelledText label="calculation-page.zone-view.label-uniform-suction" value={zone.suction.toString()} />
                </Stack>
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={1} sx={{ textAlign: 'right' }}>
            <MoreDropdown data-testid={`zone-${index}-dropdown`}>
              <DropdownOption callback={() => onClickEdit(zone)} label={t('calculation-page.zone-view.label-edit')} />
              <DropdownOption callback={() => onClickRemove(zone.id)} label={t('calculation-page.zone-view.label-remove')} />
            </MoreDropdown>
          </Grid>
          {arr.length - 1 !== index && (
            <Grid item xs={12}>
              <Divider />
            </Grid>
          )}
        </Fragment>
      ))}
    </Panel>
  );
}
